import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  createQueryParams,
  IAgentRepresentative,
  IAgentUpdateRequest,
  IBroker,
  IBrokerDuplicateGroup,
  IBrokerInsurerRequest,
  IBrokerMergeHistoryRecord,
  IBrokerMergePreview,
  IBrokerMergeResult,
  IBrokerMergeRollbackResult,
  IBrokerRevenueRate,
  IBrokerUpdateRequest,
  ICommission,
  ICommissionAllocationRequest,
  ICommissionQueryParams,
  ICommissionRate,
  ICommissionRateDeleteRequest,
  ICommissionRateQueryParams,
  ICommissionWallet,
  ICommissionWalletParams,
  IIntermediary,
  IIntermediaryClientAgeAnalysis,
  IIntermediaryQueryParams,
  IIntermediaryRequest,
  IIntermediaryStatistics,
  IIntermediaryStats,
  IMotorPremiumLimit,
  IntermediaryStatement,
  IPaginatedResponse,
  IPremiumLimit,
  IProduct,
  IProductQueryParams,
  IRepresentative,
  IRepresentativeRequest,
  PaginatedResponse,
  SystemPermissions,
} from '@inshuwa-client/models';
import { catchError, Observable, of } from 'rxjs';
import { RepresentativeService } from './representative.service';
import { NgHttpCachingHeaders } from 'ng-http-caching';
import { baseUrl } from '../configs/baseUrl';

@Injectable({
  providedIn: 'root',
})
export class IntermediaryService {
  constructor(
    private httpClient: HttpClient,
    private rep: RepresentativeService,
  ) {}

  addBrokerUser(
    user: Partial<IRepresentativeRequest>,
    brokerId: number,
  ): Observable<IRepresentative> {
    return this.httpClient.post<IRepresentative>(
      `${baseUrl()}/brokers/${brokerId}/representatives`,
      user,
    );
  }

  addAgentUser(
    user: Partial<IRepresentativeRequest>,
    id: number,
  ): Observable<IRepresentative> {
    return this.httpClient.post<IRepresentative>(
      `${baseUrl()}/agencies/${id}/representatives`,
      user,
    );
  }

  getBroker(id: number): Observable<IBroker> {
    return this.httpClient.get<IBroker>(`${baseUrl()}/brokers/${id}`);
  }

  /** The broker's own rule; only its users with canApproveInspection (or an admin) may change it. */
  setComprehensiveApproval(id: number, require: boolean): Observable<void> {
    return this.httpClient.put<void>(`${baseUrl()}/brokers/${id}/comprehensive-approval`, { require });
  }

  getBrokerRevenueRates(brokerId: number): Observable<IBrokerRevenueRate[]> {
    return this.httpClient.get<IBrokerRevenueRate[]>(
      `${baseUrl()}/brokers/${brokerId}/revenue-rate`,
    );
  }

  /** Atomically replaces all broker revenue rate rows. Rows absent from the list are deleted. */
  replaceBrokerRevenueRates(
    brokerId: number,
    rates: IBrokerRevenueRate[],
  ): Observable<IBrokerRevenueRate[]> {
    return this.httpClient.put<IBrokerRevenueRate[]>(
      `${baseUrl()}/brokers/${brokerId}/revenue-rates`,
      rates,
    );
  }

  /** Removes a broker revenue rate row. Pass insurerId to drop a per-insurer override; omit for the default row on the channel. */
  deleteBrokerRevenueRate(
    brokerId: number,
    channel: string,
    insurerId?: number | null,
  ): Observable<void> {
    let params = new HttpParams().set('channel', channel);
    if (insurerId != null) {
      params = params.set('insurerId', insurerId.toString());
    }
    return this.httpClient.delete<void>(
      `${baseUrl()}/brokers/${brokerId}/revenue-rate`,
      { params },
    );
  }

  getInsurerIntermediaryStats(id: number): Observable<IIntermediaryStats> {
    return this.httpClient.get<IIntermediaryStats>(
      `${baseUrl()}/insurers/${id}/stats/intermediary`,
    );
  }

  getIntermediaryStatistics(
    id: number,
    params?: IIntermediaryQueryParams,
  ): Observable<IIntermediaryStatistics> {
    return this.httpClient.get<IIntermediaryStatistics>(
      `${baseUrl()}/intermediaries/${id}/statistics`,
      {
        params: createQueryParams(params),
      },
    );
  }

  getProducts(
    query: IProductQueryParams,
  ): Observable<IPaginatedResponse<IProduct>> {
    return this.httpClient.get<IPaginatedResponse<IProduct>>(
      `${baseUrl()}/products`,
      {
        params: createQueryParams(query),
      },
    );
  }

  addCommissionRate(
    intermediaryCommission: ICommissionAllocationRequest,
  ): Observable<ICommissionRate> {
    return this.httpClient.post<ICommissionRate>(
      `${baseUrl()}/intermediaries/commissions/rates`,
      intermediaryCommission,
    );
  }

  addCommissionRates(
    intermediaryCommissions: ICommissionAllocationRequest[],
  ): Observable<ICommissionRate[]> {
    return this.httpClient.post<ICommissionRate[]>(
      `${baseUrl()}/intermediaries/commissions/rates/bulk`,
      intermediaryCommissions,
    );
  }

  updateCommissionRate(
    id: number,
    intermediaryCommission: ICommissionAllocationRequest,
  ): Observable<ICommissionRate> {
    return this.httpClient.put<ICommissionRate>(
      `${baseUrl()}/intermediaries/commissions/rates/${id}`,
      intermediaryCommission,
    );
  }

  updateCommissionRates(
    intermediaryCommissions: (ICommissionAllocationRequest & { id: number })[],
  ): Observable<ICommissionRate[]> {
    return this.httpClient.put<ICommissionRate[]>(
      `${baseUrl()}/intermediaries/commissions/rates/bulk`,
      intermediaryCommissions,
    );
  }

  getCommissionRates(
    query?: Partial<ICommissionRateQueryParams>,
  ): Observable<ICommissionRate[]> {
    return this.httpClient.get<ICommissionRate[]>(
      `${baseUrl()}/intermediaries/commissions/rates`,
      {
        params: createQueryParams(query),
        headers: {
          [NgHttpCachingHeaders.DISALLOW_CACHE]: '1',
        },
      },
    );
  }

  getCommissionRate(
    query?: Partial<ICommissionRateQueryParams>,
  ): Observable<IPaginatedResponse<ICommissionRate>> {
    return this.httpClient.get<IPaginatedResponse<ICommissionRate>>(
      `${baseUrl()}/intermediaries/commissions/rates`,
      {
        params: createQueryParams(query),
      },
    );
  }

  updateAgent(
    id: number,
    agentToUpdate: Partial<IAgentUpdateRequest>,
  ): Observable<IIntermediary> {
    agentToUpdate.insurerId ??=
      this.rep.getLoggedInInsurerRepresentative()?.insurerId;
    return this.httpClient.put<IIntermediary>(
      `${baseUrl()}/agencies/${id}`,
      agentToUpdate,
    );
  }

  updateBroker(
    id: number,
    brokerToUpdate: Partial<IBrokerUpdateRequest>,
  ): Observable<IBroker> {
    return this.httpClient.put<IBroker>(
      `${baseUrl()}/brokers/${id}`,
      brokerToUpdate,
    );
  }

  commissionWallet(
    id: number,
    params: ICommissionWalletParams,
  ): Observable<PaginatedResponse<ICommissionWallet>> {
    return this.httpClient.get<PaginatedResponse<ICommissionWallet>>(
      `${baseUrl()}/intermediaries/${id}/commissions/wallet`,
      {
        params: createQueryParams(params),
      },
    );
  }

  addIntermediary(
    intermediary: Partial<IIntermediaryRequest>,
  ): Observable<IIntermediary> {
    return this.httpClient.post<IIntermediary>(
      `${baseUrl()}/intermediaries`,
      intermediary,
    );
  }

  getIntermediaries(
    query: IIntermediaryQueryParams,
    noCache: boolean = false,
  ): Observable<IPaginatedResponse<IIntermediary>> {
    const HttpCatchingHeaders = noCache
      ? NgHttpCachingHeaders.DISALLOW_CACHE
      : NgHttpCachingHeaders.ALLOW_CACHE;
    return this.httpClient.get<IPaginatedResponse<IIntermediary>>(
      `${baseUrl()}/intermediaries`,
      {
        params: createQueryParams(query),
        headers: {
          [HttpCatchingHeaders]: '1',
        },
      },
    );
  }

  getIntermediaryById(id: number): Observable<IIntermediary> {
    return this.httpClient.get<IIntermediary>(
      `${baseUrl()}/intermediaries/${id}`,
    );
  }

  getAgentRepresentatives(id: number): Observable<IAgentRepresentative[]> {
    return this.httpClient.get<IAgentRepresentative[]>(
      `${baseUrl()}/agencies/${id}/representatives`,
    );
  }

  addBrokerToInsurer(
    selectedBroker: Partial<IBrokerInsurerRequest>,
  ): Observable<IIntermediary> {
    return this.httpClient.post<IIntermediary>(
      `${baseUrl()}/intermediaries/insurers`,
      selectedBroker,
    );
  }

  getIntermediaryClientsAgeAnalysis(
    insurerId: number,
    intermediaryId: number,
  ): Observable<PaginatedResponse<IIntermediaryClientAgeAnalysis>> {
    return this.httpClient.get<
      PaginatedResponse<IIntermediaryClientAgeAnalysis>
    >(
      `${baseUrl()}/allocations/insurers/${insurerId}/intermediaries/${intermediaryId}/report-listing/age-analysis`,
    );
  }

  downloadIntermediaryAgeAnalysis(insurerId: number, intermediaryId: number) {
    return this.httpClient.get(
      `${baseUrl()}/allocations/insurers/${insurerId}/intermediaries/${intermediaryId}/documents/age-analysis`,
      { responseType: 'blob' },
    );
  }

  getIntermediaryStatement(
    intermediaryId: number,
    insurerId: number,
  ): Observable<IntermediaryStatement[]> {
    return this.httpClient.get<IntermediaryStatement[]>(
      `${baseUrl()}/allocations/insurers/${insurerId}/intermediaries/${intermediaryId}/report-listing/statements`,
    );
  }

  downloadIntermediaryStatement(insurerId: number, intermediaryId: number) {
    return this.httpClient.get(
      `${baseUrl()}/allocations/insurers/${insurerId}/intermediaries/${intermediaryId}/documents/statements`,
      { responseType: 'blob' },
    );
  }

  getIntermediaryCommissions(
    param?: Partial<ICommissionQueryParams>,
  ): Observable<IPaginatedResponse<ICommission>> {
    return this.httpClient.get<IPaginatedResponse<ICommission>>(
      `${baseUrl()}/commissions`,
      { params: createQueryParams(param) },
    );
  }

  deleteIntermediary(
    id: number,
    insurer?: Partial<{ insurerId: number }>,
  ): Observable<unknown> {
    return this.httpClient.delete(`${baseUrl()}/intermediaries/${id}`, {
      params: createQueryParams(insurer),
    });
  }

  payIntermediaryCommissions(payload: number[]): Observable<ICommission[]> {
    return this.httpClient.put<ICommission[]>(
      `${baseUrl()}/commissions/payment/initiate`,
      payload,
    );
  }

  getMotorPremiumLimits(
    query?: Partial<{
      insurerId: number | null;
      partnerId: number | null;
      productId: number | null;
      intermediaryId: number | null;
    }>,
  ): Observable<IPremiumLimit[]> {
    return this.httpClient.get<IPremiumLimit[]>(
      `${baseUrl()}/intermediaries/premium-limits/motor`,
      {
        params: createQueryParams(query),
        headers: {
          [NgHttpCachingHeaders.DISALLOW_CACHE]: '1',
        },
      },
    );
  }

  addMotorPremiumLimit(
    limit: Partial<IMotorPremiumLimit>,
  ): Observable<IMotorPremiumLimit> {
    return this.httpClient.post<IMotorPremiumLimit>(
      `${baseUrl()}/intermediaries/premium-limits/motor`,
      limit,
    );
  }

  /** Lists or unlists a broker's products on Gari (every branch row of each product). Inshuwa admins only. */
  setMarketplaceListing(
    intermediaryId: number,
    productIds: number[],
    listed: boolean,
  ): Observable<ICommissionRate[]> {
    return this.httpClient.put<ICommissionRate[]>(
      `${baseUrl()}/intermediaries/${intermediaryId}/marketplace-listings`,
      { productIds, listed },
    );
  }

  deleteCommissionRate(request: ICommissionRateDeleteRequest) {
    return this.httpClient.post<ICommissionRate>(
      `${baseUrl()}/intermediaries/commissions/delete-rate`,
      request,
    );
  }

  getBrokerSystemPermissions(
    brokerId: number,
  ): Observable<SystemPermissions[]> {
    return this.httpClient.get<SystemPermissions[]>(
      `${baseUrl()}/brokers/${brokerId}/system-permissions`,
    );
  }

  static getPermissions(insurerId: number, intermediary: IIntermediary) {
    return intermediary.systemPermissions.find((x) => x.insurerId == insurerId);
  }

  getDuplicateBrokers(): Observable<IBrokerDuplicateGroup[]> {
    return this.httpClient.get<IBrokerDuplicateGroup[]>(
      `${baseUrl()}/admin/brokers/duplicates`,
      {
        headers: {
          [NgHttpCachingHeaders.DISALLOW_CACHE]: '1',
        },
      },
    );
  }

  previewBrokerMerge(
    brokerIds: number[],
    survivorId: number | null = null,
  ): Observable<IBrokerMergePreview> {
    return this.httpClient.post<IBrokerMergePreview>(
      `${baseUrl()}/admin/brokers/merge/preview`,
      { brokerIds, survivorId },
    );
  }

  mergeBrokers(
    brokerIds: number[],
    survivorId: number,
  ): Observable<IBrokerMergeResult> {
    return this.httpClient.post<IBrokerMergeResult>(
      `${baseUrl()}/admin/brokers/merge`,
      { brokerIds, survivorId },
    );
  }

  intermediaryExists(query: {
    email?: string;
    taxPayerId?: string;
  }): Observable<IIntermediary | null> {
    return this.httpClient
      .get<IIntermediary>(`${baseUrl()}/intermediaries/exists`, {
        params: new HttpParams({ fromObject: query as Record<string, string> }),
      })
      .pipe(catchError(() => of(null)));
  }

  getMergeHistory(): Observable<IBrokerMergeHistoryRecord[]> {
    return this.httpClient.get<IBrokerMergeHistoryRecord[]>(
      `${baseUrl()}/admin/brokers/merge/history`,
      {
        headers: {
          [NgHttpCachingHeaders.DISALLOW_CACHE]: '1',
        },
      },
    );
  }

  rollbackBrokerMerge(
    mergeRecordId: number,
  ): Observable<IBrokerMergeRollbackResult> {
    return this.httpClient.post<IBrokerMergeRollbackResult>(
      `${baseUrl()}/admin/brokers/merge/${mergeRecordId}/rollback`,
      {},
    );
  }
}
